/**
 * SPEC-ActiveTask-UI — pure UI decision logic for the Active Tasks surfaces.
 *
 * No jsdom in this repo, so every wiring-critical UI choice lives here and is
 * imported by BOTH `activetask-list.tsx` / `activetask-detail-v3.tsx` and the
 * tests in `__tests__/active-helpers.test.ts`. Mirrors `prescreen-helpers.ts`.
 */

import type { ActiveTaskRow } from "@shared/active-task";

/**
 * The literal an active task's "actual" cells show — there is no POD yet
 * mid-delivery. Mirrors prescreen's "N/A — not yet delivered" treatment.
 * Em-dash (U+2014), not a hyphen — locked by the UI acceptance checklist.
 */
export const ACTIVE_ACTUAL_NA = "in progress — not yet delivered";

/**
 * List sort (locked 2026-05-22): at-risk rows first, then alphabetically by
 * driver name. Pure + non-mutating (returns a new array).
 */
export function sortActiveRows<T extends Pick<ActiveTaskRow, "worker" | "earlyRisk">>(rows: T[]): T[] {
  return [...(rows ?? [])].sort((a, b) => {
    const aRisk = a?.earlyRisk?.atRisk ? 1 : 0;
    const bRisk = b?.earlyRisk?.atRisk ? 1 : 0;
    if (aRisk !== bRisk) return bRisk - aRisk; // at-risk first
    const aName = a?.worker?.name ?? "";
    const bName = b?.worker?.name ?? "";
    return aName.localeCompare(bName);
  });
}

/**
 * All Active Tasks times render in Eastern Time (Starling is a Toronto
 * operation). The literal timezone string lives in `client/src/lib/ops-tz.ts`
 * (SPEC-AgentList-Hardening canonical source); ACTIVE_TZ is a re-export so
 * call sites in this module keep a familiar name. Sarah refers to this as
 * "EST".
 */
import { OPS_TZ } from "./ops-tz";
export const ACTIVE_TZ = OPS_TZ;

/** "May 22, 6:00 AM" in Eastern Time; "—" for null/invalid. */
export function formatEst(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    timeZone: ACTIVE_TZ,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Time-only "6:00 AM" in Eastern Time — used for a window's end when it shares the start's day. */
export function formatEstTimeOnly(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    timeZone: ACTIVE_TZ,
    hour: "numeric",
    minute: "2-digit",
  });
}

/** The Eastern-Time calendar date ("5/22/2026") for same-day comparison. */
function estDateKey(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { timeZone: ACTIVE_TZ });
}

/**
 * Render the OnFleet delivery window (completeAfter → completeBefore) in Eastern
 * Time. Collapses the date when both ends fall on the same EST day
 * ("May 22, 6:00 AM – 2:00 PM"); shows both dates otherwise. Falls back to a
 * single time when only one bound is present, and "—" when neither is.
 */
export function formatDeliveryWindow(
  completeAfter: string | null | undefined,
  completeBefore: string | null | undefined,
): string {
  if (!completeAfter && !completeBefore) return "—";
  if (completeAfter && !completeBefore) return formatEst(completeAfter);
  if (!completeAfter && completeBefore) return `until ${formatEst(completeBefore)}`;
  const startKey = estDateKey(completeAfter!);
  const endKey = estDateKey(completeBefore!);
  const end = startKey != null && startKey === endKey
    ? formatEstTimeOnly(completeBefore)
    : formatEst(completeBefore);
  return `${formatEst(completeAfter)} – ${end}`;
}

/**
 * Early-risk badge label (Surface 3). Returns null when there is no risk so the
 * caller renders nothing.
 */
export function earlyRiskBadgeLabel(
  earlyRisk: ActiveTaskRow["earlyRisk"] | null | undefined,
): string | null {
  if (!earlyRisk || !earlyRisk.atRisk) return null;
  const mins = earlyRisk.minutesEarly;
  return `Early risk · ${mins} min before window`;
}

/**
 * The active detail footer is always pinned to "active" — Good/Bad/Unclear/
 * Resolve never render (there is no POD to judge). Mirrors prescreen's
 * footerMode "prescreen wins" lock, but the route already guarantees the mode.
 */
export function activeDetailFooterMode(): "active" {
  return "active";
}

/** Explicit empty state when nothing is in progress. */
export function isActiveListEmpty(rows: unknown[] | null | undefined): boolean {
  return !rows || rows.length === 0;
}

/**
 * AC1 — the Active Tasks list is a live set: rows live-add when a task starts
 * and live-remove when it completes/fails/unassigns. These are the broadcast
 * event types that change the set and therefore require a re-pull. Kept here
 * (not inline in `activetask-list.tsx`) so dropping one fails a test instead of
 * silently breaking live updates — the repo has no component-render harness.
 */
export const ACTIVE_LIST_REFETCH_EVENTS: ReadonlySet<string> = new Set([
  "active_started", // a row entered state 2 → live-add
  "active_removed", // left state 2 (completed/failed/unassigned/deleted) → live-remove
  "active_early_risk", // early-risk badge may newly appear on a row
  "completed", // completion flips the row out of the active set
  "new_delivery",
]);

export function shouldRefetchActiveList(eventType: string | null | undefined): boolean {
  return eventType != null && ACTIVE_LIST_REFETCH_EVENTS.has(eventType);
}

/**
 * AC4 — decide whether an incoming websocket event should raise the global
 * early-delivery toast. Two conditions: it must be the `active_early_risk`
 * event, and the delivery must not have been surfaced already (the "dismiss
 * does not re-fire" requirement; the backend also dedupes via
 * `earlyRiskAlertedAt`, this is the in-client guard). Non-mutating — the caller
 * records the id in `seen` after raising.
 */
export function shouldRaiseEarlyRiskToast(args: {
  eventType: string | null | undefined;
  deliveryId: string | null | undefined;
  seen: ReadonlySet<string>;
}): boolean {
  if (args.eventType !== "active_early_risk") return false;
  if (!args.deliveryId) return false;
  return !args.seen.has(args.deliveryId);
}
